<template>
  <div>
    <div class="text-4 font-weight-medium">
      About the Vault
    </div>
    <div class="mt-4">
      <div class="d-none d-md-flex">
        <div class="flex-1">
          <div
            class="d-flex align-center bg-vaults-lighter has-border border-vaults rounded-8 gray--text text-6 font-weight-medium px-6 py-2"
          >
            <div class="flex-1">Vault Asset</div>
            <div class="flex-2 text-right">Vault Balance</div>
            <div class="flex-2 text-right">Strategy Balance</div>
            <div class="flex-2 text-right">3-Days Avg Yield</div>
            <div class="flex-3 text-right">Strategy</div>
          </div>
          <div class="mt-4 d-flex align-center px-6 text-4" v-if="!!vaultStats">
            <div class="flex-1">
              <div class="text-uppercase font-weight-medium">
                {{ vaultInfo.identity }}
              </div>
              <div class="text-6 gray--text mt-1">
                {{ vaultInfo.text }}
              </div>
            </div>
            <div class="flex-2 text-right">
              <div>
                <span>{{
                  vaultStats.vaultBalance.balance | numberFormat("0,0.00")
                }}</span
                ><span class="text-6 gray--text text-uppercase ml-1">{{
                  vaultInfo.coin
                }}</span>
              </div>
              <div class="mt-1">
                <span class="text-6 font-weight-medium"
                  >${{
                    vaultStats.vaultBalance.usdValue | numberFormat("0,0")
                  }}</span
                ><span class="text-6 gray--text ml-1">USD</span>
              </div>
            </div>
            <div class="flex-2 text-right">
              <div>
                <span>{{
                  vaultStats.strategyBalance.balance | numberFormat("0,0.00")
                }}</span
                ><span class="text-6 gray--text text-uppercase ml-1">{{
                  vaultInfo.coin
                }}</span>
              </div>
              <div class="mt-1">
                <span class="text-6 font-weight-medium"
                  >${{
                    vaultStats.strategyBalance.usdValue | numberFormat("0,0")
                  }}</span
                ><span class="text-6 gray--text ml-1">USD</span>
              </div>
            </div>
            <div class="flex-2 text-right">
              <v-tooltip bottom color="dark" min-width="170">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <div>
                      {{
                        vaultStats.yield3Days.apy
                          | numberFormat("0,0.00", false)
                      }}%
                      <span class="text-6 gray--text">APY</span>
                    </div>
                    <div class="mt-1">
                      <span class="text-6 font-weight-medium"
                        >{{
                          vaultStats.yield3Days.ROI
                            | numberFormat("0,0.00", false)
                        }}%</span
                      >
                      <span class="gray--text ml-1 text-6">ROI</span>
                    </div>
                  </div>
                </template>
                <div class="text-6">
                  <div class="d-flex justify-space-between">
                    <div class="opacity-7">Fees ROI</div>
                    <div>
                      {{
                        vaultStats.yield3Days.feesROI
                          | numberFormat("0,0.00", false)
                      }}%
                    </div>
                  </div>
                  <div class="d-flex justify-space-between">
                    <div class="opacity-7">Harvest ROI</div>
                    <div>
                      {{
                        vaultStats.yield3Days.harvestROI
                          | numberFormat("0,0.00", false)
                      }}%
                    </div>
                  </div>
                  <v-divider
                    style="border-color: white"
                    class="mt-2 mb-2"
                  ></v-divider>
                  <div class="d-flex justify-space-between">
                    <span class="opacity-7">{{
                      vaultStats.yield3Days.bonus >= 0 ? "Slippage" : "Bonus"
                    }}</span>
                    <span>
                      <span v-if="vaultStats.yield3Days.bonus"
                        >{{
                          vaultStats.yield3Days.bonus
                            | abs
                            | numberFormat("0,0.000000")
                        }}%</span
                      >
                      <span v-else>-</span>
                    </span>
                  </div>
                </div>
              </v-tooltip>
            </div>
            <div class="flex-3 text-right">{{ vaultStats.strategy }}</div>
          </div>
        </div>
        <div class="ml-10" style="width:160px">
          <a
            href="https://docs.unagii.com"
            target="_blank"
            rel="noopener noreferrer"
            class="text-decoration-none"
          >
            <c-btn is-default class="width-100 black--text">Docs</c-btn></a
          >
        </div>
      </div>
      <div class="d-block d-md-none" v-if="!!vaultStats">
        <div>
          <div
            class="d-flex align-center justify-space-between font-weight-medium text-6"
          >
            <span class="gray--text">Vault Asset</span>
            <div class="d-flex">
              <span>{{ vaultInfo.identity }}</span>
              <span class="gray--text ml-1">({{ vaultInfo.text }})</span>
            </div>
          </div>
          <div
            class="d-flex align-center justify-space-between font-weight-medium text-6 mt-2"
          >
            <span class="gray--text">Vault Balance</span>
            <div class="d-flex">
              <div>
                <span>{{
                  vaultStats.vaultBalance.balance | numberFormat("0,0.00")
                }}</span
                ><span class="text-6 gray--text text-uppercase ml-1">{{
                  vaultInfo.coin
                }}</span>
              </div>
              <div class="ml-1">
                (<span class="text-6 font-weight-medium"
                  >${{
                    vaultStats.vaultBalance.usdValue | numberFormat("0,0")
                  }}</span
                ><span class="text-6 gray--text ml-1">USD</span>)
              </div>
            </div>
          </div>
          <div
            class="d-flex align-center justify-space-between font-weight-medium text-6 mt-2"
          >
            <span class="gray--text">Strategy Balance</span>
            <div class="d-flex">
              <div>
                <span>{{
                  vaultStats.strategyBalance.balance | numberFormat("0,0.00")
                }}</span
                ><span class="text-6 gray--text text-uppercase ml-1">{{
                  vaultInfo.coin
                }}</span>
              </div>
              <div class="ml-1">
                (<span class="text-6 font-weight-medium"
                  >${{
                    vaultStats.strategyBalance.usdValue | numberFormat("0,0")
                  }}</span
                ><span class="text-6 gray--text ml-1">USD</span>)
              </div>
            </div>
          </div>
          <div
            class="d-flex align-center justify-space-between font-weight-medium text-6 mt-2"
          >
            <span class="gray--text">3-Days Avg Yield</span>
            <div>
              <v-tooltip bottom color="dark" min-width="170">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" class="d-flex">
                    <div>
                      {{
                        vaultStats.yield3Days.apy
                          | numberFormat("0,0.00", false)
                      }}%
                      <span class="text-6 gray--text">APY</span>
                    </div>
                    <div class="ml-1">
                      <span class="text-6 font-weight-medium"
                        > - {{
                          vaultStats.yield3Days.ROI
                            | numberFormat("0,0.00", false)
                        }}%</span
                      >
                      <span class="gray--text ml-1 text-6">ROI</span>
                    </div>
                  </div>
                </template>
                <div class="text-6">
                  <div class="d-flex justify-space-between">
                    <div class="opacity-7">Fees ROI</div>
                    <div>
                      {{
                        vaultStats.yield3Days.feesROI
                          | numberFormat("0,0.00", false)
                      }}%
                    </div>
                  </div>
                  <div class="d-flex justify-space-between">
                    <div class="opacity-7">Harvest ROI</div>
                    <div>
                      {{
                        vaultStats.yield3Days.harvestROI
                          | numberFormat("0,0.00", false)
                      }}%
                    </div>
                  </div>
                  <v-divider
                    style="border-color: white"
                    class="mt-2 mb-2"
                  ></v-divider>
                  <div class="d-flex justify-space-between">
                    <span class="opacity-7">{{
                      vaultStats.yield3Days.bonus >= 0 ? "Slippage" : "Bonus"
                    }}</span>
                    <span>
                      <span v-if="vaultStats.yield3Days.bonus"
                        >{{
                          vaultStats.yield3Days.bonus
                            | abs
                            | numberFormat("0,0.000000")
                        }}%</span
                      >
                      <span v-else>-</span>
                    </span>
                  </div>
                </div>
              </v-tooltip>
            </div>
          </div>
          <div
            class="d-flex align-center justify-space-between font-weight-medium text-6 mt-2"
          >
            <span class="gray--text">Strategy</span>
            <span>{{vaultStats.strategy}}</span>
          </div>
        </div>
        <div class="mt-10">
          <a
            href="https://docs.unagii.com"
            target="_blank"
            rel="noopener noreferrer"
            class="text-decoration-none"
          >
            <c-btn is-default class="width-100 black--text">Docs</c-btn></a
          >
        </div>
      </div>
    </div>
    <div
      class="mt-10 text-5 line-height-25 font-weight-light"
      v-html="vaultInfo.aboutTheVault"
    ></div>
    <div class="text-5 font-weight-medium mt-10">How does it work?</div>
    <div
      class="mt-2 text-5 line-height-25 font-weight-light"
      v-html="vaultInfo.howItWorks"
    ></div>
  </div>
</template>

<script>
import BN from "bignumber.js";
import { mapGetters } from "vuex";
export default {
  props: {
    vaultInfo: Object,
  },
  computed: {
    ...mapGetters({
      getTokenPriceInUsd: "appConfig/getTokenPriceInUsd",
      vaultsStats: "productStats/vaultsStats",
      apys: "productStats/apys",
      vaultsStrategy: "productStats/vaultsStrategy",
    }),
    vaultStats() {
      if (!this.vaultsStats.result || !this.vaultsStrategy.result) return null;
      const tokenPrice = this.getTokenPriceInUsd(this.vaultInfo.coin);
      const apyDetails3Days =
        this.apys.vaults[this.vaultInfo.vaultType]?.[
          this.vaultInfo.coin
        ]?.details.find(item => item.days === 3) || {};
      const apyDetails15Days =
        this.apys.vaults[this.vaultInfo.vaultType]?.[
          this.vaultInfo.coin
        ]?.details.find(item => item.days === 15) || {};
      const item = this.vaultsStats.result.find(
        s =>
          s.vaultType === this.vaultInfo.vaultType &&
          s.token.toLowerCase() === this.vaultInfo.coin.toLowerCase()
      );
      return {
        strategy: this.vaultsStrategy.result[this.vaultInfo.vaultType][
          this.vaultInfo.coin
        ].nameRepresent,
        vaultBalance: {
          balance: item.balanceInVault,
          usdValue: new BN(tokenPrice)
            .multipliedBy(item.balanceInVault)
            .toNumber(),
        },
        strategyBalance: {
          balance: item.balanceInStrategy,
          usdValue: new BN(tokenPrice)
            .multipliedBy(item.balanceInStrategy)
            .toNumber(),
        },
        yield3Days: {
          apy: new BN(apyDetails3Days.apy).multipliedBy(100).toNumber(),
          ROI: new BN(apyDetails3Days.roi).multipliedBy(100).toNumber(),
          feesROI: new BN(apyDetails3Days.feeROI).multipliedBy(100).toNumber(),
          harvestROI: new BN(apyDetails3Days.harvestROI)
            .multipliedBy(100)
            .toNumber(),
          bonus: new BN(apyDetails3Days.slippage).multipliedBy(100).toNumber(),
        },
        yield15Days: {
          apy: new BN(apyDetails15Days.apy).multipliedBy(100).toNumber(),
          ROI: new BN(apyDetails15Days.roi).multipliedBy(100).toNumber(),
          feesROI: new BN(apyDetails15Days.feeROI).multipliedBy(100).toNumber(),
          harvestROI: new BN(apyDetails15Days.harvestROI)
            .multipliedBy(100)
            .toNumber(),
          bonus: new BN(apyDetails15Days.slippage).multipliedBy(100).toNumber(),
        },
      };
    },
  },
};
</script>
